/**
 * Documenting and correcting a safety flag — varningar (DECISIONS.md #46).
 *
 * Mirrors `api/src/Continuum.Api/Safety/SafetyEndpoints.cs`'s two safety-flag routes and the codes
 * `SafetyFlagContent.Validate` and `SafetyFlagWriteService` return.
 *
 * **Not every flag type can be created here, and that is #46 point 4 rather than an oversight.** A
 * `dnr` and a `treatment-limitation` carry `decided_by` and `decided_at` — who took the decision and
 * when — and phase 0 has no way to name a decider that is not the person typing. Writing one with
 * those columns null would put a DNR in the record attributed to nobody, above the fold, on a screen
 * a clinician reads in an emergency. The five unattributed types can be created; those two can only
 * be corrected, which is enough to fix an imported one's wording.
 *
 * **One active flag per type per patient, except `isolation`** (#46 point 10). A second is refused
 * with `FLAG_ALREADY_DOCUMENTED`, which carries the existing `flagId` **so the screen can offer to
 * correct that flag instead** — a clinician who meant to update the weight has not done the wrong
 * thing, they have used the wrong door.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * The flag types a first version may be written for — #46 point 4's five.
 *
 * `dnr` and `treatment-limitation` are deliberately absent. The form uses this list to build its
 * picker, so the two undocumentable types are never offered; `FLAG_TYPE_NOT_DOCUMENTABLE` exists for
 * the request that bypasses the form.
 */
export const DOCUMENTABLE_FLAG_TYPES = [
  'isolation',
  'infection-risk',
  'fall-risk',
  'allergy-status-unknown',
  'weight',
] as const

export type DocumentableFlagType = (typeof DOCUMENTABLE_FLAG_TYPES)[number]

/**
 * What the form sends. Mirrors `SafetyFlagRequest`.
 *
 * Two fields are mutually exclusive and neither is optional in the ordinary sense:
 * {@link supersedes} is required on a correction and refused on a create, {@link flagType} the other
 * way round. The type is in the record, so a correction that carried one would be a value the server
 * would have to distrust — it refuses it instead.
 */
export interface SafetyFlagSubmission {
  /** The `version_id` this correction replaces, or null on a first version. */
  readonly supersedes: string | null

  /**
   * A {@link DocumentableFlagType} on a create. **Null on a correction**, where sending one is
   * refused with `FLAG_TYPE_NOT_ALLOWED`.
   */
  readonly flagType: string | null

  /** `active`, `resolved` or `entered-in-error`. `active` on a create. */
  readonly status: string

  /**
   * The flag in brief: a weight in kilograms, an isolation type, or a treatment limitation in a
   * line. Record content in the documenter's words.
   *
   * **A weight crosses the wire as the text that was typed** (#46 point 6). `62,4` is refused with
   * `FLAG_WEIGHT_COMMA` rather than repaired, because a comma in a clinical number is a
   * factor-of-ten risk (AGENTS.md §5) and a client that silently rewrote it would be deciding what
   * the clinician meant.
   *
   * Required for `isolation` and `weight`; the other three types name nothing and must leave it
   * null.
   */
  readonly value: string | null

  /** The reasoning, where {@link value} is the brief. Record content. */
  readonly note: string | null
}

/** What a successful safety-flag write returns. Mirrors `WrittenSafetyFlag`. */
export interface WrittenSafetyFlag {
  /** The flag's `record_id`. New on a create, unchanged by a correction. */
  readonly flagId: string

  /** The new version's `version_id`. What the *next* correction must supersede. */
  readonly versionId: string

  /** 1 for a first version, then 2, 3 … */
  readonly version: number

  /** The `version_id` that was superseded, or null on a first version. */
  readonly supersedes: string | null
}

/**
 * Every code a safety-flag write can be refused with.
 *
 * Listed rather than left as `string` so a code added on the server without wording here is a
 * typecheck failure rather than a blank message on the panel that is supposed to say *DNR*.
 * `safetyFlagRefusalMessage` is exhaustive over this by construction.
 */
export const FLAG_REFUSAL_CODES = [
  'FLAG_BODY_MISSING',
  'FLAG_SUPERSEDES_MISSING',
  'FLAG_SUPERSEDES_NOT_ALLOWED',
  'FLAG_TYPE_INVALID',
  'FLAG_TYPE_NOT_ALLOWED',
  'FLAG_TYPE_NOT_DOCUMENTABLE',
  'FLAG_STATUS_INVALID',
  'FLAG_STATUS_NOT_ALLOWED',
  'FLAG_VALUE_MISSING',
  'FLAG_WEIGHT_COMMA',
  'FLAG_WEIGHT_MALFORMED',
  'FLAG_WEIGHT_NOT_POSITIVE',
  'FLAG_FIELD_TOO_LONG',
  'FLAG_ALREADY_DOCUMENTED',
  'FLAG_NOT_FOUND',
  'FLAG_RETRACTED',
  'FLAG_VERSION_STALE',
  'FLAG_NO_CHANGE',
  'PATIENT_NOT_FOUND',
  'PATIENT_RETRACTED',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type FlagRefusalCode = (typeof FLAG_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface FlagRefusalParams {
  /** `FLAG_FIELD_TOO_LONG`: which field, by its submission name, and the limit. */
  readonly field?: string
  readonly max?: number

  /**
   * The type the refusal is about.
   *
   * On `FLAG_VALUE_MISSING` it is what makes the message say which value is missing — an isolation
   * type and a weight are not the same question.
   */
  readonly flagType?: string

  /** `FLAG_STATUS_INVALID`: what was sent, quoted back. */
  readonly status?: string

  /**
   * `FLAG_ALREADY_DOCUMENTED`: the flag of this type the patient already has, and
   * `FLAG_NOT_FOUND`: the id that did not resolve.
   *
   * On the first, **this is a way forward rather than a detail**: the screen offers to correct that
   * flag, because wanting to document a weight on a patient who has one is wanting to change it.
   */
  readonly flagId?: string

  /** `FLAG_VERSION_STALE`: the version that was named. */
  readonly supersedes?: string

  /**
   * `FLAG_VERSION_STALE`: the version that is current instead — absent when the race was lost to a
   * concurrent insert. Not displayed either way; the screen refetches.
   */
  readonly current?: string

  /** `PATIENT_NOT_FOUND`: the id that did not resolve. */
  readonly patientId?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/** The server refused the write, with the code it refused by. */
export class FlagRefusedError extends Error {
  readonly code: FlagRefusalCode | 'UNKNOWN'
  readonly params: FlagRefusalParams
  readonly status: number

  constructor(code: FlagRefusalCode | 'UNKNOWN', params: FlagRefusalParams, status: number) {
    super(`Safety flag write refused: ${code}`)
    this.name = 'FlagRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class FlagUnavailableError extends Error {
  constructor() {
    super('Safety flag write unavailable')
    this.name = 'FlagUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is FlagRefusalCode {
  return typeof value === 'string' && (FLAG_REFUSAL_CODES as readonly string[]).includes(value)
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable body still becomes a {@link FlagRefusedError} with code `UNKNOWN` rather than an
 * unavailable error: the server answered, so nothing was written.
 */
async function refusalFrom(response: Response): Promise<FlagRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: FlagRefusalParams }
    }

    return new FlagRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new FlagRefusedError('UNKNOWN', {}, response.status)
  }
}

async function post(
  url: string,
  submission: SafetyFlagSubmission,
  signal: AbortSignal,
): Promise<WrittenSafetyFlag> {
  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request, not a failure. A write that
    // reached the server still happened, which is why the form does not abort a submit in flight.
    if (signal.aborted) {
      throw cause
    }
    throw new FlagUnavailableError()
  }

  // Before `refusalFrom`, which reads the body: a 401 is the session having ended rather than a
  // refusal of this flag.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as WrittenSafetyFlag
  } catch {
    // A 201 whose body did not parse: the row is in the record and this client cannot say which
    // version it became. Unavailable rather than failure, so the clinician reopens the patient and
    // reads what the record says instead of documenting the same flag twice.
    throw new FlagUnavailableError()
  }
}

/**
 * Documents a safety flag that was not in the record: one `clinical.safety_flag` version 1 and its
 * audit row, in one transaction on the server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). A flag documented by mistake is corrected
 * to `entered-in-error`, and both versions stay readable.
 */
export function documentSafetyFlag(
  patientId: string,
  submission: SafetyFlagSubmission,
  signal: AbortSignal,
): Promise<WrittenSafetyFlag> {
  return post(`/api/patients/${encodeURIComponent(patientId)}/safety-flags`, submission, signal)
}

/**
 * Corrects a safety flag already in the record: the next version of the same `record_id`.
 *
 * The value, the note and the status can move. The **type cannot** — a fall risk corrected into a
 * DNR would be a different fact about the patient wearing the first one's identity, and the two
 * decider columns cannot move either, since nothing here may attribute a decision (#46 point 2).
 */
export function correctSafetyFlag(
  patientId: string,
  flagId: string,
  submission: SafetyFlagSubmission,
  signal: AbortSignal,
): Promise<WrittenSafetyFlag> {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/safety-flags/${encodeURIComponent(flagId)}/versions`,
    submission,
    signal,
  )
}
